import { useCallback, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, UserPlus } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams } from '@/components/DataTable/types'
import { fetchGroupJoinRequests, type AdminJoinRequest } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A4.09 — Qo'shilish so'rovlari. GET /admin/groups/:id/join-requests (backend `8310afb`).
 * `join_by_request` yoqilgan guruh/kanalning kutilayotgan so'rovlari (a'zolik hali berilmagan).
 */
export function GroupJoinRequests() {
  const t = useT()
  const { id = '' } = useParams()

  const columns = useMemo<AdminColumn<AdminJoinRequest>[]>(
    () => [
      {
        key: 'username',
        header: t.col.name,
        type: 'text',
        minWidth: 180,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.first_name || r.username || `#${r.user_id}`}
            {r.username ? <span className="ml-1 text-tertiary">@{r.username}</span> : null}
          </Link>
        ),
      },
      { key: 'bio', header: t.groups.bio, type: 'text', minWidth: 220, sortable: false },
      { key: 'created_at', header: t.groups.requestedAt, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchGroupJoinRequests(Number(id), params), [id])

  return (
    <>
      <PageHeader
        title={t.groups.joinRequests}
        screenId="A4.09"
        actions={
          <Link
            to={`/groups/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.groups.cardTitle}
          </Link>
        }
      />
      <DataTable<AdminJoinRequest>
        screenId="A4.09"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.user_id)}
        exportable
        emptyState={{ icon: <UserPlus size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
